import store from '@state/store'
import postAuthRedirect from './../utils/post-auth-redirect'
import premiumFunnelOrigin from './../utils/premium-funnel-origin'
import { AUTH_ROUTES } from './auth-routes'

import Login from '@views/auth/login'
import Signup from '@views/auth/signup'
import ForgotPassword from '@views/auth/forgot-password'
import SetNewPassword from '@views/auth/set-new-password'

import Settings from '@views/settings'

import Homepage from '@views/homepage'
import Chart from '@views/charts/chart'
import Charts from '@views/charts'
import Playlist from '@views/playlists/playlist'
import Playlists from '@views/playlists'
import BrowseGenres from '@views/browse-genres'
import Genre from '@views/genre/genre'
import BrowseMixes from '@views/browse-mixes'
import Mix from '@views/mix'

import Search from '@views/search'
import SearchRecent from '@components/search/recent'
import SearchResults from '@components/search/results'

import Channels from '@views/channels'
import Channel from '@views/channels/channel'

import YourLibraryMixes from '@views/your-library-mixes'
import YourLibraryArtists from '@views/your-library-artists'
import YourLibraryShows from '@views/your-library-shows'
import YourLibraryGenres from '@views/your-library-genres'

import Show from '@views/show'
import Artist from '@views/artist'

import Account from './views/account'
import AccountOverview from './views/account/overview'
import AccountChangePassword from './views/account/change-password'
import AccountEditProfile from './views/account/edit-profile'
import ManageSubscription from './views/account/manage-subscription'
import CancelPremium from './views/account/cancel-premium'
import CancellationFeedback from './views/account/cancellation-feedback'

import Premium from './views/premium/premium'
import PremiumWelcome from './views/premium/welcome'
import PremiumPlans from './views/premium/view-plans'
import PremiumSelectPayment from './views/premium/select-payment'
import PremiumComplete from './views/premium/complete'
import DonationComplete from './views/premium/donation-complete'

import Onboarding from './views/onboarding'

function beforeEnterAuth(redirectOnLogout) {
    return function (routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
            // Go to page as expected
            next()
        } else {
            // Redirect to login page
            redirectOnLogout ? next({ name: 'login' }) : next({ name: 'login', params: { redirect: routeTo.fullPath } })
        }
    }
}

function preventUnauthenticated() {
    return function (routeTo, routeFrom, next) {
        if (store.getters['auth/loggedIn']) next()
        else next({ name: 'home' })
    }
}

// Payment pages are strictly post-auth: a signed-out visitor (old link,
// bookmark, mid-funnel plan pick) is sent to sign-up first and returned to
// this exact payment page — plan id in the path, postAuth marker so the
// payment page can dissolve the funnel if they turn out to be Premium.
export function beforeEnterPremiumCheckout(routeTo, routeFrom, next) {
    if (store.getters['auth/loggedIn']) return next()
    const joiner = routeTo.fullPath.includes('?') ? '&' : '?'
    postAuthRedirect.set(`${routeTo.fullPath}${joiner}postAuth=1`)
    next({ name: 'signup' })
}

// Where the funnel started belongs to the whole /premium cluster, not to the
// plans page: a visitor can enter at the welcome page, a deep-linked payment
// page, or donate. vue-router runs a record's beforeEnter only when that record
// is newly activated, so this fires on entry from outside and never on
// navigation between the funnel's own pages — which is what keeps a payment-page
// round trip from overwriting the origin.
export function beforeEnterPremiumFunnel(routeTo, routeFrom, next) {
    // A cold boot carrying the postAuth marker is a funnel continuation: auth
    // completed and sent the visitor back here, so the pre-auth origin must
    // survive. Otherwise an entry with no known origin drops any stale one,
    // and a return from the auth flow leaves the live origin alone.
    if (!routeTo.query.postAuth) {
        if (!routeFrom.name) premiumFunnelOrigin.clear()
        else if (!AUTH_ROUTES.includes(routeFrom.name)) premiumFunnelOrigin.set(routeFrom.fullPath)
    }
    next()
}

export default [
    {
        name: 'login',
        path: '/auth/login',
        component: Login,
        meta: { noindex: true },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            if (store.getters['auth/loggedIn']) {
                next({ name: 'home' })
            } else {
                next()
            }
        },
    },
    {
        name: 'signup',
        path: '/auth/signup',
        component: Signup,
        meta: { noindex: true },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            if (store.getters['auth/loggedIn']) {
                next({ name: 'home' })
            } else {
                next()
            }
        },
    },
    {
        name: 'forgot-password',
        path: '/auth/forgot-password',
        component: ForgotPassword,
        meta: { noindex: true },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            if (store.getters['auth/loggedIn']) {
                next({ name: 'home' })
            } else {
                next()
            }
        },
    },
    {
        name: 'reset-password',
        path: '/auth/reset-password',
        alias: '/set-new-password',
        component: SetNewPassword,
        meta: { noindex: true },
        props: true,
    },
    {
        path: '/account',
        component: Account,
        meta: { noindex: true },
        beforeEnter: beforeEnterAuth(true),
        children: [
            {
                name: 'account/overview',
                path: '',
                component: AccountOverview,
            },
            {
                name: 'account/change-password',
                path: 'change-password',
                component: AccountChangePassword,
            },
            {
                name: 'account/edit-profile',
                path: 'edit-profile',
                component: AccountEditProfile,
            },
            {
                name: 'account/manage-subscription',
                path: 'manage-subscription',
                component: ManageSubscription,
            },
            {
                name: 'account/cancel-premium',
                path: 'cancel-premium',
                component: CancelPremium,
            },
            {
                name: 'account/cancellation-feedback',
                path: 'cancellation-feedback',
                component: CancellationFeedback,
            },
        ],
    },
    {
        name: 'settings',
        path: '/settings',
        component: Settings,
        meta: { noindex: true },
        props: {
            mainComponent: 'AudioQuality',
        },
        beforeEnter: beforeEnterAuth(true),
    },
    {
        name: 'logout',
        path: '/logout',
        meta: { noindex: true },
        beforeEnter(routeTo, routeFrom, next) {
            store.dispatch('auth/logOut')
        },
    },

    {
        name: 'home',
        path: '/',
        component: Homepage,
    },
    {
        name: 'browse/charts/index',
        path: '/browse/charts',
        component: Charts,
    },
    {
        name: 'browse/charts',
        path: '/browse/charts/:id',
        component: Chart,
    },
    {
        name: 'browse/playlists/index',
        path: '/browse/playlists',
        component: Playlists,
    },
    {
        name: 'browse/playlists',
        path: '/browse/playlists/:id',
        component: Playlist,
    },
    {
        name: 'browse/genres',
        path: '/browse/genres',
        component: BrowseGenres,
    },
    {
        name: 'genre',
        path: '/genre/:slug',
        component: Genre,
    },
    {
        name: 'browse/mixes/latest',
        alias: '/browse/mixes',
        path: '/browse/mixes/latest',
        component: BrowseMixes,
        props: {
            mainComponent: 'LatestMixes',
        },
    },
    {
        name: 'browse/mixes/trending',
        path: '/browse/mixes/trending',
        component: BrowseMixes,
        props: {
            mainComponent: 'TrendingMixes',
        },
    },
    {
        name: 'browse/mixes/recently',
        path: '/browse/mixes/recently',
        component: BrowseMixes,
        props: {
            mainComponent: 'RecentlyPlayedMixes',
        },
    },
    {
        name: 'mix',
        path: '/mix/:id/',
        component: Mix,
    },

    {
        name: 'search',
        path: '/search',
        component: Search,
        redirect: '/search',
        children: [
            {
                name: 'search/recent',
                path: '',
                component: SearchRecent,
            },
            {
                name: 'search/results/top',
                path: 'results/top/:keyword?',
                component: SearchResults,
                meta: { noindex: true },
                props: {
                    mainComponent: 'Top',
                },
            },
            {
                name: 'search/results/mixes',
                path: 'results/mixes/:keyword?',
                component: SearchResults,
                meta: { noindex: true },
                props: {
                    mainComponent: 'Mixes',
                },
            },
            {
                name: 'search/results/genres',
                path: 'results/genres/:keyword?',
                component: SearchResults,
                meta: { noindex: true },
                props: {
                    mainComponent: 'Genres',
                },
            },
            {
                name: 'search/results/artists',
                path: 'results/artists/:keyword?',
                component: SearchResults,
                meta: { noindex: true },
                props: {
                    mainComponent: 'Artists',
                },
            },
            {
                name: 'search/results/shows',
                path: 'results/shows/:keyword?',
                component: SearchResults,
                meta: { noindex: true },
                props: {
                    mainComponent: 'Shows',
                },
            },
            {
                name: 'search/results/tracklist',
                path: 'results/tracklist/:keyword?',
                component: SearchResults,
                meta: { noindex: true },
                props: {
                    mainComponent: 'Tracklist',
                },
            },
        ],
    },

    {
        name: 'channel-index',
        path: '/radio',
        component: Channels,
    },
    {
        name: 'channel-index/favorites',
        path: '/radio/favorites',
        component: Channels,
        meta: { noindex: true },
        props: {
            isFavorites: true,
        },
        beforeEnter: beforeEnterAuth(),
    },
    {
        name: 'channel',
        path: '/radio/:channelId/',
        component: Channel,
        props: {
            mainComponent: 'Overview',
        },
    },
    {
        name: 'channel/schedule',
        path: '/radio/:channelId/schedule',
        component: Channel,
        props: {
            mainComponent: 'Schedule',
        },
    },
    {
        name: 'channel/history',
        path: '/radio/:channelId/history',
        component: Channel,
        props: {
            mainComponent: 'History',
        },
    },

    {
        path: '/your-library/mixes',
        name: 'your-library/mixes',
        component: YourLibraryMixes,
        meta: { noindex: true },
        beforeEnter: beforeEnterAuth(true),
    },
    {
        path: '/your-library/artists',
        name: 'your-library/artists',
        component: YourLibraryArtists,
        meta: { noindex: true },
        beforeEnter: beforeEnterAuth(true),
    },
    {
        path: '/your-library/shows',
        name: 'your-library/shows',
        component: YourLibraryShows,
        meta: { noindex: true },
        beforeEnter: beforeEnterAuth(true),
    },
    {
        path: '/your-library/genres',
        name: 'your-library/genres',
        component: YourLibraryGenres,
        meta: { noindex: true },
        beforeEnter: beforeEnterAuth(true),
    },

    {
        path: '/shows/:id',
        name: 'show',
        component: Show,
        props: {
            mainComponent: 'Overview',
        },
    },
    {
        path: '/shows/:id/about',
        name: 'show/about',
        component: Show,
        props: {
            mainComponent: 'About',
        },
    },
    {
        name: 'artist',
        path: '/artist/:artistUrl?/:id',
        component: Artist,
        props: {
            mainComponent: 'Overview',
        },
    },
    {
        name: 'artist/about',
        path: '/artist/:artistUrl?/:id/about',
        component: Artist,
        props: {
            mainComponent: 'About',
        },
    },
    {
        path: '/premium',
        component: Premium,
        alias: '/offer/premium',
        meta: { noindex: true },
        beforeEnter: beforeEnterPremiumFunnel,
        children: [
            {
                name: 'premium/welcome',
                path: '',
                component: PremiumWelcome,
            },
            {
                name: 'premium/view-plans',
                path: 'view-plans',
                component: PremiumPlans,
                props: {
                    mainComponent: 'ViewPlans',
                },
            },
            {
                name: 'premium/donate',
                path: 'donate',
                component: PremiumPlans,
                props: {
                    mainComponent: 'Donate',
                },
            },
            {
                name: 'premium/select-payment',
                path: 'select-payment/:planId',
                component: PremiumSelectPayment,
                beforeEnter: beforeEnterPremiumCheckout,
            },
            {
                name: 'premium/complete',
                path: 'complete',
                component: PremiumComplete,
            },
            {
                name: 'premium/donate/complete',
                path: 'donate/complete',
                component: DonationComplete,
            },
        ],
    },
    {
        name: 'onboarding',
        path: '/onboarding',
        component: Onboarding,
        meta: { noindex: true },
        beforeEnter: preventUnauthenticated(),
    },

    {
        path: '/404',
        name: '404',
        component: require('@views/404').default,
        meta: { noindex: true },
        // Allows props to be passed to the 404 page through route
        // params, such as `resource` to define what wasn't found.
        props: true,
    },
    // Redirect any unmatched routes to the 404 page. This may
    // require some server configuration to work in production:
    // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
    {
        path: '*',
        meta: { noindex: true },
        redirect: '404',
    },
]
